/**
 * Domain metadata + pure helpers for the Home and Lens surfaces.
 *
 * This module is strictly metadata + math -- no numeric fixtures. When the
 * API has no data for a field, the consuming component renders an N/A state
 * rather than pulling a fake number from here.
 */

export type DomainKey = 'rsq' | 'ffs' | 'vbc' | 'ipa';
export type TimePeriod = 30 | 60 | 90 | 180 | 730;

/** Static metadata for a documentation domain. Display-only. */
export interface DomainMeta {
  key: DomainKey;
  label: string;
  fullName: string;
  /** One-line explanation of what this domain measures. */
  blurb: string;
}

export const DOMAIN_META: DomainMeta[] = [
  {
    key: 'rsq',
    label: 'RSQ®',
    fullName: 'Risk, Safety, Quality',
    blurb: 'Risk-factor + decision-rule documentation across high-acuity chief complaints.',
  },
  {
    key: 'ffs',
    label: 'FFS',
    fullName: 'Fee for Service',
    blurb: 'Coding completeness and capture rate for fee-for-service billing.',
  },
  {
    key: 'vbc',
    label: 'VBC',
    fullName: 'Value Based Care',
    blurb: 'Care-transition, preventive, and chronic-disease documentation under VBC contracts.',
  },
  {
    key: 'ipa',
    label: 'IPA',
    fullName: 'Inpatient Admission',
    blurb: 'Obs-vs-admit criteria, two-midnight justification, and admission narrative.',
  },
];

/** Back-compat alias. Components should migrate to `DOMAIN_META`. */
export type DomainSummary = DomainMeta;
export const DOMAIN_SUMMARIES = DOMAIN_META;

export type ScoreBucket = 'excellent' | 'good' | 'needs' | 'unavailable';

/**
 * Map a score to a coarse visual bucket. Returns `'unavailable'` when null
 * or undefined so the UI can render neutral styling instead of pretending a
 * missing number is a low number.
 */
export const scoreBucket = (score: number | null | undefined): ScoreBucket => {
  if (score == null) return 'unavailable';
  if (score >= 80) return 'excellent';
  if (score >= 65) return 'good';
  return 'needs';
};

export type TrendDir = 'up' | 'down' | 'stable' | 'unavailable';

export const trendDirection = (trend: number | null | undefined): TrendDir => {
  if (trend == null) return 'unavailable';
  if (trend > 0.1) return 'up';
  if (trend < -0.1) return 'down';
  return 'stable';
};

export const isDomainKey = (value: string | undefined): value is DomainKey =>
  value === 'rsq' || value === 'ffs' || value === 'vbc' || value === 'ipa';

export const findDomain = (key: string | undefined): DomainMeta | undefined =>
  DOMAIN_META.find((d) => d.key === key);
